import { makeStyles, Tooltip } from '@material-ui/core';
import { Button as MaterialButton } from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { ReactElement, useMemo } from 'react';
import { NoteType } from '../../contexts/personal-notes-context';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { COLORS } from '../../theme/variables';

const useStyles = makeStyles(() => ({
  icon: {
    color: COLORS.COLOR_GRAY_BASE,
    width: 24,
    height: 24,
    cursor: 'pointer',
  },
  button: {
    minWidth: 0,
  },
  buttonIcon: {
    width: 20,
    height: 20,
  },
  tooltip: {
    maxWidth: 150,
    textAlign: 'left',
  },
}));

type NoteAccessConfig = {
  visible: string[];
  notVisible: string[];
};

const NOTE_ACCESS_CONFIG: Record<NoteType, NoteAccessConfig> = {
  [NoteType.MentorNote]: {
    visible: ['Admins only'],
    notVisible: [
      'Any {rb("mentors")}',
      'Any founders',
      'This particular {rb("mentor")}',
    ],
  },
  [NoteType.FounderNote]: {
    visible: ['Admins only'],
    notVisible: [
      'Any {rb("mentors")}',
      'Any founders',
      'This particular founder',
    ],
  },
  [NoteType.VentureNote]: {
    visible: [
      'Admins',
      '{rb("mentors-u")} assigned to the venture',
      'Tagged {rb("mentors")}',
    ],
    notVisible: ['Any founders', 'Unassigned untagged {rb("mentors")}'],
  },
  [NoteType.EventNote]: {
    visible: [
      'Admins',
      'Founders assigned to the venture',
      '{rb("mentors-u")} assigned to the venture',
    ],
    notVisible: [
      'Founders from other ventures',
      '{rb("mentors-u")} not assigned to the venture',
    ],
  },
  [NoteType.GeneralNote]: {
    visible: ['Owner of the note', 'Tagged users'],
    notVisible: ['Admins', 'Untagged {rb("mentors")}', 'Untagged founders'],
  },
};

export interface NoteAccessIndicatorProps {
  noteType?: keyof typeof NoteType;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  variant?: 'icon' | 'button';
}

function processResourceBundlePlaceholders(
  text: string,
  rb: (key: string) => string,
): string {
  return text.replace(/\{rb\("([^"]+)"\)\}/g, (_, key) => rb(key));
}

function NoteAccessIndicator({
  noteType,
  placement = 'bottom',
  variant = 'icon',
}: NoteAccessIndicatorProps): ReactElement {
  const classes = useStyles();
  const { rb } = useResourceBundles();

  const content = useMemo(() => {
    if (!noteType) return null;

    const config = NOTE_ACCESS_CONFIG[noteType];
    if (!config) return null;

    return (
      <>
        <span>Visible to:</span>
        <br />
        {config.visible.map((item, index) => (
          <span key={index}>
            - {processResourceBundlePlaceholders(item, rb)}
            <br />
          </span>
        ))}
        <br />
        <span>NOT visible to:</span>
        <br />
        {config.notVisible.map((item, index) => (
          <span key={index}>
            - {processResourceBundlePlaceholders(item, rb)}
            <br />
          </span>
        ))}
      </>
    );
  }, [noteType, rb]);

  const Icon = content ? LockIcon : LockOpenIcon;
  const tooltipContent = content || 'Visible to all users.';

  if (variant === 'button') {
    return (
      <Tooltip
        title={tooltipContent}
        placement={placement}
        enterTouchDelay={0}
        leaveTouchDelay={0}
        classes={{ tooltip: classes.tooltip }}>
        <MaterialButton color='primary' className={classes.button}>
          <Icon className={classes.buttonIcon} />
        </MaterialButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={tooltipContent}
      placement={placement}
      enterTouchDelay={0}
      leaveTouchDelay={0}
      classes={{ tooltip: classes.tooltip }}>
      <Icon className={classes.icon} />
    </Tooltip>
  );
}

export default NoteAccessIndicator;
